import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy } from "react";

import ROUTES from "../constants/routes";
import MainLayout from "../layouts/mainLayout";
import ProtectedRoute from "../guards/ProtectedRoute";
import GuestRoute from "../guards/GuestRoute";

const DashboardPage = lazy(() => import("../pages/dashboard"));
const LoginPage = lazy(() => import("../pages/auth/Login"));
const TeacherClassesPage = lazy(() => import("../pages/teacherDashboard/teacherClasses"));
const TeacherClassDetailPage = lazy(() => import("../pages/teacherDashboard/teacherClassDetail"));
const TeacherAssignmentsPage = lazy(() => import("../pages/teacherDashboard/teacherAssignments"));
const ExamDefinitionPage = lazy(() => import("../pages/teacherDashboard/teacherActiveTest"));
const TeacherExamsPage = lazy(() => import("../pages/teacherDashboard/teacherExams"));
const TeacherNotificationsPage = lazy(() => import("../pages/teacherDashboard/teacherNotifications"));
const TeacherWeeklySchedulePage = lazy(() => import("../pages/teacherDashboard/teacherWeeklySchedule"));
const StudentListPage = lazy(() => import("../pages/classManagement/studentList"));
const StudentLevelPage = lazy(() => import("../pages/classManagement/studentLevel"));
const StudentAttendancePage = lazy(() => import("../pages/classManagement/studentAttendance"));
const StudentBehaviorPage = lazy(() => import("../pages/classManagement/studentBehavior"));
const ReportCardPage = lazy(() => import("../pages/classManagement/reportCard"));
const AssignmentDefinitionPage = lazy(() =>
  import("../pages/assignmentManagement/assignmentDefinition/assignmentDefinition")
);
const ChallengeDefinitionPage = lazy(() =>
  import("../pages/challengeManagement/challengeDefinition/challengeDefinition")
);
const TeacherChallengesPage = lazy(() => import("../pages/teacherDashboard/teacherChallenges"));
const AiExamGenerationPage = lazy(() =>
  import("../pages/aiAssistant/examGeneration")
);
const AiAssignmentGenerationPage = lazy(() =>
  import("../pages/aiAssistant/assignmentGeneration")
);
const AiImageGenerationPage = lazy(() =>
  import("../pages/aiAssistant/imageGeneration")
);
const AiLessonPlanGenerationPage = lazy(() =>
  import("../pages/aiAssistant/lessonPlanGeneration")
);
const AiLessonSummaryPage = lazy(() =>
  import("../pages/aiAssistant/lessonSummary")
);
const ContentManagementPage = lazy(() =>
  import("../pages/contentManagement/contentManagement")
);
const NotFoundPage = lazy(() => import("../pages/notFound"));

const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: (
      <GuestRoute>
        <LoginPage />
      </GuestRoute>
    ),
  },
  {
    path: ROUTES.REGISTER,
    element: <Navigate to={ROUTES.LOGIN} replace />,
  },
  {
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: ROUTES.DASHBOARD, element: <DashboardPage /> },
      { path: ROUTES.TEACHER_CLASSES, element: <TeacherClassesPage /> },
      { path: ROUTES.TEACHER_CLASS_DETAIL, element: <TeacherClassDetailPage /> },
      { path: "/teacher-dashboard/classes/:classId", element: <Navigate to={ROUTES.TEACHER_CLASSES} replace /> },
      { path: "/teacher-dashboard/assignments", element: <Navigate to={ROUTES.TEACHER_ASSIGNMENTS} replace /> },
      { path: "/teacher-dashboard/active-tests", element: <Navigate to={ROUTES.TEACHER_ACTIVE_TESTS} replace /> },
      { path: ROUTES.TEACHER_ASSIGNMENTS, element: <TeacherAssignmentsPage /> },
      { path: ROUTES.EXAM_DEFINITION, element: <ExamDefinitionPage /> },
      { path: ROUTES.TEACHER_ACTIVE_TESTS, element: <TeacherExamsPage /> },
      { path: ROUTES.TEACHER_SCHOOL_NOTIFICATIONS, element: <TeacherNotificationsPage /> },
      { path: ROUTES.TEACHER_WEEKLY_SCHEDULE, element: <TeacherWeeklySchedulePage /> },
      { path: ROUTES.CLASS_STUDENTS, element: <StudentListPage /> },
      { path: ROUTES.CLASS_LEVELING, element: <StudentLevelPage /> },
      { path: ROUTES.CLASS_ATTENDANCE, element: <StudentAttendancePage /> },
      { path: ROUTES.CLASS_BEHAVIOR, element: <StudentBehaviorPage /> },
      { path: ROUTES.CLASS_REPORT_CARD, element: <ReportCardPage /> },
      { path: ROUTES.ASSIGNMENT_DEFINITION, element: <AssignmentDefinitionPage /> },
      { path: ROUTES.CHALLENGE_DEFINITION, element: <ChallengeDefinitionPage /> },
      { path: ROUTES.TEACHER_CHALLENGES, element: <TeacherChallengesPage /> },
      { path: ROUTES.AI_EXAM_GENERATION, element: <AiExamGenerationPage /> },
      { path: ROUTES.AI_ASSIGNMENT_GENERATION, element: <AiAssignmentGenerationPage /> },
      { path: ROUTES.AI_IMAGE_GENERATION, element: <AiImageGenerationPage /> },
      { path: ROUTES.AI_LESSON_PLAN_GENERATION, element: <AiLessonPlanGenerationPage /> },
      { path: ROUTES.AI_LESSON_SUMMARY, element: <AiLessonSummaryPage /> },
      { path: ROUTES.CONTENT_MANAGEMENT, element: <ContentManagementPage /> },
    ],
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
]);

export default router;
